/**
 * Holds the access token for the lifetime of the page, in memory only.
 *
 * Deliberately not `localStorage` or `sessionStorage`. Anything written there is readable by any
 * script that runs on the page, so a single cross-site scripting flaw anywhere in the application
 * — or in a dependency — would hand an attacker a working credential. A module-level variable is
 * gone the moment the tab closes and is not reachable from injected script that does not already
 * have the module's scope.
 *
 * The long-lived half of the session lives in an `HttpOnly` cookie the server sets, which script
 * cannot read at all. On a page reload this store is empty, and the application recovers the
 * session by exchanging that cookie for a fresh access token.
 */

type Listener = (token: string | null) => void;

let accessToken: string | null = null;
const listeners = new Set<Listener>();

/**
 * The current access token.
 *
 * @returns the token, or null when there is no established session on this page
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Replaces the stored token and notifies subscribers.
 *
 * @param token the new token, or null to clear it
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) {
    listener(token);
  }
}

/** Clears the stored token. */
export function clearAccessToken(): void {
  setAccessToken(null);
}

/**
 * Subscribes to token changes.
 *
 * This is how the React layer learns that the transport gave up on a session — a refresh that
 * fails clears the token from deep inside a fetch, far from any component.
 *
 * @param listener called with every new token value
 * @returns a function that removes the subscription
 */
export function subscribeToAccessToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
